import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { View } from '../types';

export function usePaginationState(views: View[], bookmarkedViews: View[]) {
  // Default to a larger per page count for better performance with large datasets
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(24);
  const [bookmarkCurrentPage, setBookmarkCurrentPage] = useState(1);
  const [bookmarkPerPage, setBookmarkPerPage] = useState(24);
  
  const location = useLocation();
  const navigate = useNavigate();
  
  // Initialize from URL if available
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    
    // Main pagination
    const pageParam = params.get('page');
    const perPageParam = params.get('perPage');
    
    if (pageParam) {
      const page = parseInt(pageParam, 10);
      if (!isNaN(page) && page > 0) {
        setCurrentPage(page);
      }
    }
    
    if (perPageParam) {
      const itemsPerPage = parseInt(perPageParam, 10);
      if (!isNaN(itemsPerPage) && itemsPerPage > 0) {
        setPerPage(itemsPerPage);
      }
    }
    
    // Bookmark pagination
    const bookmarkPageParam = params.get('bookmark_page');
    const bookmarkPerPageParam = params.get('bookmark_perPage');
    
    if (bookmarkPageParam) {
      const page = parseInt(bookmarkPageParam, 10);
      if (!isNaN(page) && page > 0) {
        setBookmarkCurrentPage(page);
      }
    }
    
    if (bookmarkPerPageParam) {
      const itemsPerPage = parseInt(bookmarkPerPageParam, 10);
      if (!isNaN(itemsPerPage) && itemsPerPage > 0) {
        setBookmarkPerPage(itemsPerPage);
      }
    }
  }, [location.search]);
  
  // Reset to first page when views change (but not when perPage changes)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const currentPageParam = params.get('page');
    
    // Only reset if we're on a page higher than would be valid with the new views length
    const maxValidPage = Math.max(1, Math.ceil(views.length / perPage));
    const currentPageValue = currentPageParam ? parseInt(currentPageParam, 10) : currentPage;
    
    if (currentPageValue > maxValidPage) {
    setCurrentPage(1);
      
      // Update URL to reflect the page change
      const newParams = new URLSearchParams(params);
      newParams.set('page', '1');
      navigate({ search: newParams.toString() }, { replace: true });
    }
  }, [views.length, perPage, navigate, location.search, currentPage]);
  
  // Reset to first page when bookmarked views change
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const currentPageParam = params.get('bookmark_page');
    
    // Only reset if we're on a page higher than would be valid with the new views length
    const maxValidPage = Math.max(1, Math.ceil(bookmarkedViews.length / bookmarkPerPage));
    const currentPageValue = currentPageParam ? parseInt(currentPageParam, 10) : bookmarkCurrentPage;
    
    if (currentPageValue > maxValidPage) {
    setBookmarkCurrentPage(1);
      
      // Update URL to reflect the page change
      const newParams = new URLSearchParams(params);
      newParams.set('bookmark_page', '1');
      navigate({ search: newParams.toString() }, { replace: true });
    }
  }, [bookmarkedViews.length, bookmarkPerPage, navigate, location.search, bookmarkCurrentPage]);
  
  // Calculate pagination for main views
  const totalViews = views.length;
  
  // Calculate total pages based on the actual number of views and per page count
  const totalPages = Math.max(1, Math.ceil(totalViews / perPage));
  
  // Ensure current page is valid
  const validCurrentPage = Math.min(currentPage, totalPages);
  
  // Calculate the correct start and end indices
  const startIndex = (validCurrentPage - 1) * perPage;
  const endIndex = Math.min(startIndex + perPage, totalViews);
  
  // Get the paginated views for the current page
  const paginatedViews = views.slice(startIndex, endIndex);
  
  // Calculate pagination for bookmarked views
  const totalBookmarkViews = bookmarkedViews.length;
  const totalBookmarkPages = Math.max(1, Math.ceil(totalBookmarkViews / bookmarkPerPage));
  
  // Ensure bookmark current page is valid
  const validBookmarkCurrentPage = Math.min(bookmarkCurrentPage, totalBookmarkPages);
  
  const bookmarkStartIndex = (validBookmarkCurrentPage - 1) * bookmarkPerPage;
  const bookmarkEndIndex = Math.min(bookmarkStartIndex + bookmarkPerPage, totalBookmarkViews);
  const paginatedBookmarkedViews = bookmarkedViews.slice(bookmarkStartIndex, bookmarkEndIndex);
  
  // Handle page changes
  const handlePageChange = (page: number) => {
    // Ensure the page is valid
    const validPage = Math.min(Math.max(1, page), totalPages);
    setCurrentPage(validPage);
    
    // Update URL
    const params = new URLSearchParams(location.search);
    
    // Preserve view parameter
    const viewParam = params.get('view');
    
    // Create new params
    const newParams = new URLSearchParams();
    newParams.set('page', validPage.toString());
    newParams.set('perPage', perPage.toString());
    
    // Add view parameter if it exists
    if (viewParam) {
      newParams.set('view', viewParam);
    }
    
    navigate({ search: newParams.toString() }, { replace: true });
    
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  
  const handlePerPageChange = (newPerPage: number) => {
    // Calculate what page we should be on to keep approximately the same items in view
    const firstVisibleItemIndex = (validCurrentPage - 1) * perPage;
    const newPage = Math.floor(firstVisibleItemIndex / newPerPage) + 1;
    
    // Update the state
    setPerPage(newPerPage);
    setCurrentPage(Math.min(newPage, Math.ceil(totalViews / newPerPage)));
    
    // Update URL
    const params = new URLSearchParams(location.search);
    
    // Preserve view parameter
    const viewParam = params.get('view');
    
    // Create new params
    const newParams = new URLSearchParams();
    newParams.set('page', newPage.toString());
    newParams.set('perPage', newPerPage.toString());
    
    // Add view parameter if it exists
    if (viewParam) {
      newParams.set('view', viewParam);
    }
    
    navigate({ search: newParams.toString() }, { replace: true });
    
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  
  // Handle bookmark page changes
  const handleBookmarkPageChange = (page: number) => {
    // Ensure the page is valid
    const validPage = Math.min(Math.max(1, page), totalBookmarkPages);
    setBookmarkCurrentPage(validPage);
    
    // Update URL
    const params = new URLSearchParams(location.search);
    
    // Preserve view parameter
    const viewParam = params.get('view');
    
    // Create new params
    const newParams = new URLSearchParams();
    newParams.set('bookmark_page', validPage.toString());
    newParams.set('bookmark_perPage', bookmarkPerPage.toString());
    
    // Add view parameter if it exists
    if (viewParam) {
      newParams.set('view', viewParam);
    }
    
    navigate({ search: newParams.toString() }, { replace: true });
    
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  
  const handleBookmarkPerPageChange = (newPerPage: number) => {
    // Calculate what page we should be on to keep approximately the same items in view
    const firstVisibleItemIndex = (validBookmarkCurrentPage - 1) * bookmarkPerPage;
    const newPage = Math.floor(firstVisibleItemIndex / newPerPage) + 1;
    
    // Update the state
    setBookmarkPerPage(newPerPage);
    setBookmarkCurrentPage(Math.min(newPage, Math.ceil(totalBookmarkViews / newPerPage)));
    
    // Update URL
    const params = new URLSearchParams(location.search);
    
    // Preserve view parameter
    const viewParam = params.get('view');
    
    // Create new params
    const newParams = new URLSearchParams();
    newParams.set('bookmark_page', newPage.toString());
    newParams.set('bookmark_perPage', newPerPage.toString());
    
    // Add view parameter if it exists
    if (viewParam) {
      newParams.set('view', viewParam);
    }
    
    navigate({ search: newParams.toString() }, { replace: true });
    
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  
  return {
    // Main pagination
    currentPage: validCurrentPage,
    perPage,
    totalPages,
    totalViews,
    paginatedViews,
    handlePageChange,
    handlePerPageChange,
    
    // Bookmark pagination
    bookmarkCurrentPage: validBookmarkCurrentPage,
    bookmarkPerPage,
    totalBookmarkPages,
    totalBookmarkViews,
    paginatedBookmarkedViews,
    handleBookmarkPageChange,
    handleBookmarkPerPageChange,
  };
} 